import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertTriangle, ArrowRight, Package, LayoutGrid, List } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ITEMS_PER_PAGE = 20;

const STATUS_CONFIG = {
  quoted:       { label: 'Quoted',       bg: 'bg-violet-500/10',  text: 'text-violet-600 dark:text-violet-400',   dot: 'bg-violet-500',   kanban: 'bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20' },
  posted:       { label: 'Posted',       bg: 'bg-purple-500/10',  text: 'text-purple-600 dark:text-purple-400',   dot: 'bg-purple-500',   kanban: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20' },
  dispatched:   { label: 'Dispatched',   bg: 'bg-orange-500/10',  text: 'text-orange-600 dark:text-orange-400',   dot: 'bg-orange-500',   kanban: 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20' },
  picked_up:    { label: 'Picked Up',    bg: 'bg-indigo-500/10',  text: 'text-indigo-600 dark:text-indigo-400',   dot: 'bg-indigo-500',   kanban: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-500/20' },
  delivered:    { label: 'Delivered',    bg: 'bg-teal-500/10',    text: 'text-teal-600 dark:text-teal-400',       dot: 'bg-teal-500',     kanban: 'bg-teal-500/10 text-teal-600 dark:text-teal-400 border-teal-500/20'  },
  cancelled:    { label: 'Cancelled',    bg: 'bg-red-500/10',     text: 'text-red-600 dark:text-red-400',         dot: 'bg-red-500',      kanban: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20'      },
};

const STATUSES = Object.entries(STATUS_CONFIG).map(([value, cfg]) => ({ value, ...cfg }));
const COLS = 'grid grid-cols-[1fr_110px_96px_148px_36px] items-center gap-4 px-6';

function StatusPill({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: 'Closed', bg: 'bg-slate-500/10', text: 'text-slate-500', dot: 'bg-slate-400' };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${cfg.bg} ${cfg.text}`}>
      <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

export default function LoadsPage() {
  const { token } = useAuth();
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list');
  const [filterStatus, setFilterStatus] = useState('all');
  
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const scrollContainerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(0);

  const fetchLoads = useCallback(async (pageNum, isNewFilter = false) => {
    try {
      setLoading(true);
      let url = `${API_URL}/api/admin/loads?page=${pageNum}&limit=${ITEMS_PER_PAGE}`;
      if (filterStatus !== 'all') {
        url += `&status=${filterStatus}`;
      }

      const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (response.ok) {
        const newData = await response.json();
        setHasMore(newData.length === ITEMS_PER_PAGE);
        setLoads(prev => isNewFilter ? newData : [...prev, ...newData]);
      }
    } catch (err) {
      console.error('Error fetching loads:', err);
    } finally {
      setLoading(false);
    }
  }, [token, filterStatus]);

  useEffect(() => {
    setPage(1);
    setHasMore(true);
    fetchLoads(1, true);
  }, [filterStatus]);

  const loadMoreData = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchLoads(nextPage, false);
  };

  const getLoadsByStatus = (status) => loads.filter(l => l.status === status);

  const scroll = (dir) => {
    scrollContainerRef.current?.scrollBy({ left: dir === 'left' ? -300 : 300, behavior: 'smooth' });
  };

  const handleMouseDown = (e) => {
    if (!scrollContainerRef.current) return;
    setIsDragging(true);
    setDragStart(e.pageX - scrollContainerRef.current.offsetLeft);
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !scrollContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    scrollContainerRef.current.scrollLeft -= (x - dragStart);
    setDragStart(x);
  };

  const handleMouseUp = () => setIsDragging(false);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    if (isDragging) {
      container.addEventListener('mousemove', handleMouseMove);
      container.addEventListener('mouseup', handleMouseUp);
      container.addEventListener('mouseleave', handleMouseUp);
      container.style.cursor = 'grabbing';
    } else {
      container.style.cursor = 'grab';
    }
    return () => {
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('mouseleave', handleMouseUp);
    };
  }, [isDragging, dragStart]);

  return (
    <div className="space-y-6" data-testid="admin-loads">
      {/* Header Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-outfit text-2xl font-bold text-foreground">Loads</h1>
          <p className="text-sm text-muted-foreground">Manage transport requests</p>
        </div>

        <div className="flex items-center gap-3">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[150px] bg-transparent border-border">
              <SelectValue placeholder="Filter status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex border border-border rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('kanban')}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors ${
                viewMode === 'kanban' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <LayoutGrid className="h-4 w-4" /> Kanban
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors border-l border-border ${
                viewMode === 'list' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <List className="h-4 w-4" /> List
            </button>
          </div>
        </div>
      </div>

      {loads.length === 0 && loading ? (
        <div className="text-center py-20 text-muted-foreground">Loading…</div>
      ) : viewMode === 'kanban' ? (
        /* Kanban View Component */
        <div className="space-y-2">
          <div className="flex items-center justify-between pb-2">
            <p className="text-sm text-muted-foreground">Drag to scroll · Loaded {loads.length} rows</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="h-8 px-3" onClick={() => scroll('left')}>←</Button>
              <Button variant="outline" size="sm" className="h-8 px-3" onClick={() => scroll('right')}>→</Button>
            </div>
          </div>
          <div
            ref={scrollContainerRef}
            className="w-full overflow-x-auto overflow-y-hidden pb-4"
            onMouseDown={handleMouseDown}
            style={{ userSelect: isDragging ? 'none' : 'auto' }}
          >
            <div className="flex gap-4 min-w-max">
              {STATUSES.map((status) => {
                const statusLoads = getLoadsByStatus(status.value);
                return (
                  <div key={status.value} className="w-72 flex-shrink-0">
                    <div className={`rounded-xl px-4 py-2.5 mb-3 border flex items-center justify-between ${status.kanban}`}>
                      <span className="font-semibold text-sm">{status.label}</span>
                      <span className="text-xs font-bold opacity-70">{statusLoads.length}</span>
                    </div>
                    <div className="space-y-3">
                      {statusLoads.map((load) => (
                        <Link key={load.id} to={`/admin/loads/${load.id}`} className="block">
                          <Card className="bg-card border-border hover:border-accent/30 hover:shadow-md transition-all duration-200 cursor-pointer">
                            <CardContent className="p-4">
                              <div className="flex items-start justify-between mb-2">
                                <p className="font-semibold text-sm text-foreground">
                                  {load.year} {load.vehicle_make} {load.vehicle_model}
                                </p>
                                {load.risk_flags?.length > 0 && (
                                  <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground mb-3">
                                {load.pickup_zip} → {load.delivery_zip}
                              </p>
                              <div className="flex items-center justify-between">
                                <Badge variant="outline" className="text-xs capitalize">{load.transport_type}</Badge>
                                <span className="font-bold text-sm text-foreground">${load.customer_price?.toFixed(0)}</span>
                              </div>
                            </CardContent>
                          </Card>
                        </Link>
                      ))}
                      {statusLoads.length === 0 && (
                        <div className="text-center py-8 text-muted-foreground/50 text-sm border border-dashed border-border rounded-xl">
                          No loads
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        /* List View Component */
        <div className="bg-card border border-border rounded-2xl shadow-sm overflow-hidden">
          <div className={`${COLS} py-3 border-b border-border bg-muted/40`}>
            <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Vehicle / Route</span>
            <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider text-center">Type</span>
            <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider text-right">Price</span>
            <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider text-center">Status</span>
            <span />
          </div>

          {loads.length === 0 ? (
            <div className="text-center py-20 text-muted-foreground">
              <Package className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p>No loads found</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {loads.map((load) => (
                <Link
                  key={load.id}
                  to={`/admin/loads/${load.id}`}
                  className={`${COLS} py-4 hover:bg-muted/40 transition-colors`}
                >
                  <div className="min-w-0">
                    <p className="font-semibold text-sm text-foreground truncate">
                      {load.year} {load.vehicle_make} {load.vehicle_model}
                    </p>
                    <div className="flex items-center gap-1 mt-0.5 text-xs text-muted-foreground">
                      <span>{load.pickup_zip}</span>
                      <ArrowRight className="h-3 w-3 shrink-0" />
                      <span>{load.delivery_zip}</span>
                    </div>
                  </div>

                  <div className="flex justify-center">
                    <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-muted text-xs font-medium text-muted-foreground capitalize">
                      {load.transport_type || '—'}
                    </span>
                  </div>

                  <div className="text-right">
                    <span className="font-bold text-sm text-foreground tabular-nums">
                      ${load.customer_price?.toFixed(0) ?? '—'}
                    </span>
                  </div>

                  <div className="flex justify-center">
                    <StatusPill status={load.status} />
                  </div>

                  <div className="flex justify-center">
                    {load.risk_flags?.length > 0 ? <AlertTriangle className="h-4 w-4 text-amber-500" strokeWidth={1.5} /> : <span className="h-4 w-4" />}
                  </div>
                </Link>
              ))}

              {hasMore && (
                <div className="flex justify-center py-5 bg-muted/10">
                  <Button variant="outline" size="sm" disabled={loading} onClick={loadMoreData}>
                    {loading ? "Loading..." : "Load More Records"}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}